import { createContext, useState, useRef, useContext, useEffect } from 'react'
import useCurrentLocationContext from './currentLocationContext'
import { useLocation, useNavigate } from 'react-router-dom'
import useMapDataContext, { MapController, MapView } from '@contexts/mapDataContext'
import { useAuthContext } from './authContext'
import useCableContext from './cableContext'
import useModalContext from './modalContext'
import { debounce } from 'throttle-debounce'
import snakecaseKeys from 'snakecase-keys'
import useStateRef from 'react-usestateref'
import useLoading from '@hooks/useLoading'
import { colors } from '@styles/theme'
import axios from '@utils/webRequests'
import mapboxgl from '!mapbox-gl' // eslint-disable-line import/no-webpack-loader-syntax

const TravelModeContext = createContext()
const TravelModeProvider = ({ children }) => {
  const { trueLocation, setCurrentLocation, isLocationPermissionDenied } = useCurrentLocationContext()
  const { state: authState, handleInstantUpdate, authenticated } = useAuthContext()
  const { map, mapRef, profilesInitialized } = useMapDataContext()
  const { connectSelf, disappearSelf } = useCableContext()
  const { subscriptionFeature } = useModalContext()
  const location = useLocation()
  const navigate = useNavigate()
  const isMap = authenticated && (location.pathname === '' || location.pathname === '/' || location.pathname === '/map')
  const { isLoading: isLoadingNearbyPositions, isLoadingDelayed: isLoadingNearbyPositionsDelayed, setIsLoading: setIsLoadingNearbyPositions } = useLoading()
  const { isLoading: isLoadingNearbyUsers, isLoadingDelayed: isLoadingNearbyUsersDelayed, setIsLoading: setIsLoadingNearbyUser } = useLoading()
  const [isTravelModeSelectorEnabled, setIsTravelModeSelectorEnabled, isTravelModeSelectorEnabledRef] = useStateRef(false)
  const [nearbyProfileCount, setNearbyProfileCount] = useState(null)
  const travelMarkersRef = useRef([])

  // When travel mode selector is enabled, this gets the nearby 
  useEffect(() => {
    if (map && isTravelModeSelectorEnabled) {
      // if no profile count currently exists, then grab nearby positions immediately
      // (happens when we first turn on travel mode selector)
      if (nearbyProfileCount === null) getNearbyPositions()

      // Debounce nearby position callback to prevent too many calls on map moving, and set to listener
      const debouncedGetNearbyPositions = debounce(1000, getNearbyPositions)
      map.on("moveend", debouncedGetNearbyPositions)

      // Cleanup listener on useEffect exit
      return () => map?.off('moveend', debouncedGetNearbyPositions)
    }
  }, [map, isTravelModeSelectorEnabled])

  // Turns off travel mode selector when we leave the map screen, or if location permissions become rejected
  // This should cause the user position useEffect to rerun and clear map markers.
  useEffect(() => {
    if ((!isMap || isLocationPermissionDenied) && isTravelModeSelectorEnabled) {
      turnOffTravelModeSelector()
    }
  }, [isMap, isLocationPermissionDenied, isTravelModeSelectorEnabled])

  const getNearbyPositions = () => {
    // WARNING: We need to double check that the selector is actually on because this called within a listener callback.
    //          IF WE DON'T, then the function often fires after the selector has been turned off, and causes markers to be placed on the map again
    if (!isTravelModeSelectorEnabledRef.current) return;
    setIsLoadingNearbyPositions(true)
    const { lng, lat } = mapRef.current.getCenter()
    axios.get(`api/users/nearby_profiles_positions?lonlat=[${lng},${lat}]`)
    .then((res) => {
        if (!isTravelModeSelectorEnabledRef.current) return;
        replaceTravelMarkers(res.data)
        setNearbyProfileCount(res.data?.length)
        setIsLoadingNearbyPositions(false)
      })
      .catch(console.log)
  }

  const setNewLocation = () => {
    if (!authState?.user?.isSubscribed) { 
      // If the user is not subscribed, open subscription feature modal
      subscriptionFeature.open()
      return;
    }
    setIsLoadingNearbyUser(true)
    const { lng, lat } = mapRef.current.getCenter()
    const data = snakecaseKeys({ lonlat: [lng, lat], isTravelModeEnabled: true })
    let userData = null

    axios.put(`/api/my/profiles/set_travel_mode`, data)
      .then((res) => {
        // Save user data for updating later. This prevents the UI from updating before the selector has disappeared
        userData = res.data
        // Call/chain the method to get nearby users now we know the location set successfully
        return MapController.requestNearbyUsers([lng, lat], userData)
      })
      .then(() => {
        // Set the user in auth state to information on the backend
        handleInstantUpdate(userData)
        // Sets the currentLocation for the currentLocationContext
        setCurrentLocation([lng, lat])
        // Cleanup the internal state for the component
        turnOffTravelModeSelector()
        // set loading state to false
        setIsLoadingNearbyUser(false)
        // handle appearance and disappearance for users around you
        websocketDisappearAndReappear([lng, lat])
      })
  }

  const clearTravelMode = () => {
    // The trueLocation should be set in the current location context
    // when travel mode is on, and we need it to "quickly" get the user's location
    if (!trueLocation) return;
    setIsLoadingNearbyUser(true)
    const [lng, lat] = trueLocation
    const data = snakecaseKeys({ lonlat: [lng, lat], isTravelModeEnabled: false })
    let userData = null

    axios.put(`/api/my/profiles/set_travel_mode`, data)
      .then((res) => {
        // Save user data for updating later. This prevents the UI from updating before the selector has disappeared
        userData = res.data
        // Call/chain the method to get nearby users now we know the location set successfully
        return MapController.requestNearbyUsers(userData.lonlat, userData)
      })
      .then(() => {
        // Set the user in auth state to information on the backend
        handleInstantUpdate(userData)
        // Sets the currentLocation for the currentLocationContext
        setCurrentLocation(userData.lonlat)
        // Cleanup the internal state for the component
        turnOffTravelModeSelector()
        // Sets the  map location back to the user's position before traveling
        mapRef.current.jumpTo({
          center: userData.lonlat,
          zoom: 13.5,
        })
        // set loading state to false
        setIsLoadingNearbyUser(false)
        // handle appearance and disappearance for users around you
        websocketDisappearAndReappear(userData.lonlat)
      })
  }

  const replaceTravelMarkers = (newPositions) => {
    travelMarkersRef.current.forEach((marker) => marker?.remove())
    travelMarkersRef.current = []
    newPositions?.forEach((lonlat) => {
      const newMarker = new mapboxgl.Marker({ color: colors.statusColors.right_now('primary'), scale: 0.5 })
        .setLngLat(lonlat)
        .addTo(mapRef.current)
      travelMarkersRef.current.push(newMarker)
    })
  }
  
  const turnOnTravelModeSelector = () => {
    if ((!authState?.user?.isSubscribed && authState?.user?.isHidden) || !profilesInitialized || isLocationPermissionDenied) return;
    if (location.pathname === '/grid') {
      navigate('/map')
    }
    MapView.setUserVisibility(false)
    setIsTravelModeSelectorEnabled(true)
  }
  
  const turnOffTravelModeSelector = () => {
    travelMarkersRef.current.forEach((marker) => marker?.remove())
    travelMarkersRef.current = []
    setNearbyProfileCount(null)
    setIsTravelModeSelectorEnabled(false)
    MapView.setUserVisibility(true)
  }

  const websocketDisappearAndReappear = (lonlat) => {
    // Makes your profile disappear to users in the previous location
    disappearSelf()
    // Makes your profile appear to users in the new location
    // The timeout here gives the disappear wss request time to finish
    setTimeout(() => connectSelf(lonlat), 1000)
  }

  return (
    <TravelModeContext.Provider value={{
      nearbyProfileCount,
      travelMarkersRef,

      turnOnTravelModeSelector,
      turnOffTravelModeSelector,
      isTravelModeSelectorEnabled,

      setNewLocation,
      clearTravelMode,
      getNearbyPositions,
      isLoadingNearbyPositionsDelayed,
      isLoadingNearbyUsers,
      isLoadingNearbyUsersDelayed,
    }}>
      {children}
    </TravelModeContext.Provider>
  )
}

const useTravelModeContext = () => useContext(TravelModeContext)

export default useTravelModeContext

export { TravelModeContext, TravelModeProvider, useTravelModeContext }
